import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSelector } from "reselect";
import { useIntl } from "react-intl";

//Components
import { Container, Paper, Grid } from "@mui/material";
import { HasPermission } from 'Core/security';
//Global components
import { Breadcrumb } from "Components";
import { TagsGroup } from "Components/Tag";
import OdometerView from 'Modules/.common/components/OdometerView';

//Helpers
import { useRealtimeDrivers } from 'Core/hooks';
import {
    formatOffsetDisplayOdometer,
    formatOffsetDisplayEngineHours,
} from "Core/data/Helpers";

//Actions
import { catalogLoad, unitEditUnitOpen } from "Redux/actions";

//Global styles
import styles from "Assets/sass/core/vars.scss";

//Styles
import './styles.scss'

//local components
import ScheduleServices from "./components/scheduledServices";
import PerformedServices from "./components/performedServices";
import EditUnit from 'Modules/settings/views/unitSettings/components/editUnitSettings';

import { useDataGridThrottlerWithTrackedIds } from 'Core/hooks'
import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);
const getUnitGroupsListCatalog = (state) => state.catalogsRedux.unitGroupsList;

const getData = createSelector([getUnitsCatalog, getReadingsCatalog, getUnitGroupsListCatalog], (units, readings, unitGroups) => {
    return units.map((unit) => {
        const lastReading = readings[unit.id]
        const asset = unit.asset;
        let newUnitGroups = [];
        if (unit?.unitGroups) {
            unit?.unitGroups?.forEach(unitGroup => {
                let unitGroupExist = unitGroups?.find(ug => ug?.id == unitGroup?.unitgroup_id);
                if (unitGroupExist) {
                    newUnitGroups.push(unitGroupExist);
                }
            });
        }

        const filteredUnit = {
            ...unit,
            unitGroups: newUnitGroups,
            devicesGroups: (lastReading && lastReading.deviceGroups) || [],
            engineOdometer: lastReading?.engineOdometer,
            ecuEngineOdometer: lastReading?.ecuEngineOdometer,
            engineHours: lastReading?.engineHours,
            ecuEngineHours: lastReading?.ecuEngineHours,

            vin: asset?.vin || lastReading?.vin || "",
            ecuVin: lastReading?.ecuVin,

            driverId: lastReading?.driverId || unit.driverId,

            make: asset?.make || lastReading?.make,
            model: asset?.model || lastReading?.model,
            year: asset?.year || lastReading?.year,
        };

        filteredUnit.displayOdometer = formatOffsetDisplayOdometer(filteredUnit);
        filteredUnit.displayHours = formatOffsetDisplayEngineHours(filteredUnit);

        return filteredUnit;
    });
}
);

export const VehicleDetail = (props) => {

    const dispatch = useDispatch()

    const [view, setView] = useState("scheduled");
    const { messages } = useIntl();
    const location = useLocation();

    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const user = useSelector(state => state.securityRedux.user);
    const openServiceModal = useSelector(state => state.editUnitRedux.isOpen)

    let path = location.pathname.split("/");
    const id = path[path.length - 1];

    const [driver, setDriver] = useState(null);

    const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);

    const [vehicle, setVehicle] = useState(null);

    //create a computed data from catalog
    const computedData = useSelector(state => getData(state) || []);
    const propertiesToValidate = useMemo(() => [
        "label",
        "engineOdometer",
        "engineHours",
        "vin",
        "make",
        "model",
        "year",
        "useEcuOdometer",
        "useEcuEngineHours",
        "useEcuVin",
        "devicesGroups",
        "unitGroups",
        "engineOdometer",
        "engineHours",
        "driverId",
        "ecuVin",
        "offsetEcuEngineOdometer",
        "offsetEngineOdometer",
        "offsetEcuEngineHours",
        "offsetEngineHours",
    ], []);

    // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
    const [data, changeIdsCallback] = useDataGridThrottlerWithTrackedIds(
        computedData,
        1000,
        () => { },
        "id",
        propertiesToValidate
    );

    useEffect(() => {
        //get Vehicle selected from computed data
        const vehicle = data && data.find((x) => x.id.toString() === id.toString());
        setVehicle(vehicle);
    }, [data]);

    //initial component
    useEffect(() => {
        dispatch(catalogLoad(["drivers", "unitGroupsList"]))
        changeIdsCallback([{ id: id }]);
    }, []);

    useEffect(() => {
        const driver = drivers.find(
            (driver) =>
                vehicle &&
                vehicle.driverId &&
                driver.driverId.toString() == vehicle.driverId.toString()
        );
        setDriver(driver);
    }, [drivers, vehicle]);

    useEffect(() => {
        setIsUnitModalOpen(openServiceModal);
    }, [props.openServiceModal]);

    const refreshTable = (newView) => {
        if (newView != view) {
            setView(newView);
        }
    };
    const viewStyles = {
        ecuChip: {
            fontSize: "10px",
            background: styles.ecu_not,
            color: "white",
            display: "inline-flex",
            alignItems: "center",
            marginRight: "8px",
            marginTop: "8px",
            fontWeight: "bold",
            padding: "3px",
        },
    };


    const views = (type) => {
        let types = {
            'scheduled': <ScheduleServices
                id={id}
                vehicle={vehicle}
                showTable={refreshTable}
                view={view}
            />,
            'performed': <PerformedServices
                id={id}
                vehicle={vehicle}
                showTable={refreshTable}
                view={view}
            />
        }
        return types[type]
    }

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: "15px 20px" }}
                >
                    <Grid item xs={3} md={4}>
                        <Breadcrumb
                            links={[
                                {
                                    title: messages["mainView_vehicles"],
                                    link: "/app/maintenance/main",
                                },
                                {
                                    title: <span className="nav-link cursor-pointer" onClick={() => { if (HasPermission(user, "", [64])) { dispatch(unitEditUnitOpen(vehicle.id)) } }}>{vehicle?.label}</span>
                                },
                            ]}
                        />
                    </Grid>
                    {vehicle && (
                        <>
                            <Grid item>
                                <div style={{ display: 'flex', justifyContent: 'end' }}>
                                    <span style={{ marginRight: driver?.name ? 5 : 10 }} className="span-text-title">
                                        {messages["vehicleDetailView_assignedDriver"]}
                                    </span>
                                    <span className="span-text-content">
                                        {driver && driver.name}
                                    </span>
                                </div>
                            </Grid>
                            <Grid item>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', marginRight: 8 }}>
                                    <div className="span-text-title">
                                        {messages["vehicleDetailView_tags"]}
                                    </div>
                                    <div>
                                        <TagsGroup
                                            options={vehicle?.tags || []}
                                            nameField="label"
                                            colorField="color"
                                        />
                                    </div>
                                </div>
                            </Grid>
                        </>
                    )}
                </Grid>
                {vehicle && (
                    <div style={{ marginLeft: 10, marginRight: 10 }}>
                        <Grid
                            item
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            className="vehicle-detail-subheader"
                        >
                            <Grid item>
                                <span className="span-text-title">
                                    {messages["vehicleDetailView_makeModel"]}
                                </span>
                                <span className="span-text-content">
                                    {vehicle.make}
                                    {vehicle.make && vehicle.model ? "/" : ""}
                                    {vehicle.model}
                                </span>
                            </Grid>

                            <Grid item>
                                <span className="span-text-title">
                                    {messages["vehicleDetailView_year"]}
                                </span>
                                <span className="span-text-content">{vehicle.year}</span>
                            </Grid>

                            <Grid item>
                                <OdometerView {...vehicle} isKph={user?.isKph} />
                            </Grid>

                            <Grid item>
                                {(vehicle.jbus ||
                                    vehicle.vbus ||
                                    vehicle.useEcuEngineHours) && (
                                        <div
                                            style={{
                                                ...viewStyles.ecuChip,
                                                background: vehicle.useEcuEngineHours
                                                    ? styles.ecu
                                                    : styles.ecu_not,
                                            }}
                                        >
                                            <span>ECU</span>
                                        </div>
                                    )}
                                <span className="span-text-title">
                                    {messages["vehicleDetailView_engineHours"]}
                                </span>
                                <span className="span-text-content">
                                    {vehicle.displayHours}
                                </span>
                            </Grid>

                            <Grid item>
                                {(vehicle.jbus || vehicle.vbus || vehicle.useEcuVin) && (
                                    <div
                                        style={{
                                            ...viewStyles.ecuChip,
                                            background: vehicle.useEcuVin
                                                ? styles.ecu
                                                : styles.ecu_not,
                                        }}
                                    >
                                        <span>ECU</span>
                                    </div>
                                )}
                                <span className="span-text-title">
                                    {messages["vehicleDetailView_vin"]}
                                </span>
                                <span className="span-text-content">
                                    {vehicle.useEcuVin ? vehicle.ecuVin : vehicle.vin}
                                </span>
                            </Grid>
                        </Grid>
                    </div>
                )}
                <div style={{ padding: "0px 20px 20px 20px" }}>
                    {
                        views(view)
                    }
                </div>
            </Paper>
            <EditUnit />
        </Container>
    );
};

export default VehicleDetail
